/**
 * New Trip wizard.
 *
 * Directive §16: five steps — select visits, select teams, date and start time,
 * plan automatically, review.
 *
 * Directive §4 / §16 / §102: no technical routing terminology reaches the
 * operator. There is no clustering setting, no distance algorithm, no
 * optimization parameter, no k-medoids, no VRPTW, no 2-opt. The progression
 * copy below is the approved plain-language dictionary and is the ONLY
 * vocabulary this screen uses for planning.
 *
 * Directive §6: the mobile bottom navigation is hidden throughout.
 */

import { useEffect, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';

export type WizardStep = 1 | 2 | 3 | 4 | 5;

/** §16 Step 4. Plain language only — never the algorithm names. */
export const PLANNING_STAGES = [
  'Finding shared addresses',
  'Grouping visits into team territories',
  'Ordering each route',
  'Checking drive times and schedule',
  'Preparing navigation',
] as const;

export interface SelectableVisit {
  id: string;
  addressLine: string;
  city: string;
  recordSummary: string;
  selected: boolean;
}

export interface SelectableTeam {
  id: string;
  name: string;
  memberCount: number;
  selected: boolean;
}

export interface TeamPlanSummary {
  teamId: string;
  teamName: string;
  stopCount: number;
  visitCount: number;
  miles: number;
  windowLabel: string;
  /** Non-null when this team's day overruns or has a conflict. §16 */
  warning: string | null;
}

export interface PlanReview {
  visitCount: number;
  stopCount: number;
  teamCount: number;
  roadMiles: number;
  longestTeamDayLabel: string;
  assignmentStatus: string;
  scheduleConflicts: number;
  routeStatus: string;
  /** True when the road network was unavailable and figures are estimates. §18 */
  estimated: boolean;
  teams: TeamPlanSummary[];
}

export function TripWizard({
  step, visits, teams, unresolvedCount, date, startTime, review, planning,
  onStepChange, onToggleVisit, onToggleTeam, onDateChange, onStartTimeChange,
  onPlan, onOpenRoutes, onViewMap, onViewSchedule, onAdvancedAnalysis, onCancel,
}: {
  step: WizardStep;
  visits: SelectableVisit[];
  teams: SelectableTeam[];
  unresolvedCount: number;
  date: string;
  startTime: string;
  review: PlanReview | null;
  planning: boolean;
  onStepChange: (s: WizardStep) => void;
  onToggleVisit: (id: string) => void;
  onToggleTeam: (id: string) => void;
  onDateChange: (d: string) => void;
  onStartTimeChange: (t: string) => void;
  onPlan: () => void;
  onOpenRoutes: () => void;
  onViewMap: () => void;
  onViewSchedule: () => void;
  onAdvancedAnalysis: () => void;
  onCancel: () => void;
}) {
  const selectedVisits = visits.filter((v) => v.selected).length;
  const selectedTeams = teams.filter((t) => t.selected).length;

  const canAdvance =
    step === 1 ? selectedVisits > 0 :
    step === 2 ? selectedTeams > 0 :
    step === 3 ? Boolean(date && startTime) :
    step === 4 ? review !== null :
    step === 5 ? review !== null :
    false;

  return (
    <div className="pt-4 pb-[calc(88px+env(safe-area-inset-bottom))]">
      <div className="flex items-center gap-3 mb-1">
        <h6 className="text-accent-700">Step {step} of 5</h6>
        <button className="btn btn-ghost ml-auto" onClick={onCancel}>Cancel</button>
      </div>

      <h1 className="mb-4">
        {step === 1 && 'Select visits'}
        {step === 2 && 'Select teams'}
        {step === 3 && 'Date and start time'}
        {step === 4 && 'Planning'}
        {step === 5 && 'Review'}
      </h1>

      {step === 1 && (
        <StepSelectVisits
          visits={visits}
          unresolvedCount={unresolvedCount}
          onToggle={onToggleVisit}
        />
      )}

      {step === 2 && <StepSelectTeams teams={teams} onToggle={onToggleTeam} />}

      {step === 3 && (
        <StepDateTime
          date={date} startTime={startTime}
          onDateChange={onDateChange} onStartTimeChange={onStartTimeChange}
        />
      )}

      {step === 4 && <StepPlanning planning={planning} done={review !== null} />}

      {step === 5 && review && (
        <StepReview
          review={review}
          onViewMap={onViewMap}
          onViewSchedule={onViewSchedule}
          onAdvancedAnalysis={onAdvancedAnalysis}
        />
      )}

      <WizardFooter
        step={step}
        canAdvance={canAdvance}
        planning={planning}
        onBack={() => onStepChange(Math.max(1, step - 1) as WizardStep)}
        onNext={() => {
          if (step === 3) { onPlan(); onStepChange(4); return; }
          if (step === 5) { onOpenRoutes(); return; }
          onStepChange(Math.min(5, step + 1) as WizardStep);
        }}
      />
    </div>
  );
}

function StepSelectVisits({
  visits, unresolvedCount, onToggle,
}: { visits: SelectableVisit[]; unresolvedCount: number; onToggle: (id: string) => void }) {
  return (
    <div>
      {/* §16 §20: unresolved stops are excluded from planning but stay visible,
          with a count so the operator knows work is waiting. */}
      {unresolvedCount > 0 && (
        <p className="mb-4 p-3 bg-alert-100 border border-alert-300 rounded-lg text-base">
          {unresolvedCount} {unresolvedCount === 1 ? 'location needs' : 'locations need'} review
          and will not be routed.
        </p>
      )}

      <p className="text-base text-neutral-700 mb-3">
        {visits.filter((v) => v.selected).length} of {visits.length} selected
      </p>

      <ul>
        {visits.map((v) => (
          <li key={v.id} className="border-b border-neutral-300">
            <label className="flex items-start gap-3 py-3 min-h-touch cursor-pointer">
              <input
                type="checkbox"
                checked={v.selected}
                onChange={() => onToggle(v.id)}
                className="mt-1 w-5 h-5 accent-accent"
              />
              <span className="flex-1 min-w-0">
                <span className="block font-heading font-semibold text-h4">{v.addressLine}</span>
                <span className="block text-base text-neutral-700 truncate">
                  {v.city} · {v.recordSummary}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepSelectTeams({
  teams, onToggle,
}: { teams: SelectableTeam[]; onToggle: (id: string) => void }) {
  return (
    <ul>
      {teams.map((t) => (
        <li key={t.id} className="border-b border-neutral-300">
          <label className="flex items-center gap-3 py-3 min-h-touch cursor-pointer">
            <input
              type="checkbox"
              checked={t.selected}
              onChange={() => onToggle(t.id)}
              className="w-5 h-5 accent-accent"
            />
            <span className="flex-1">
              <span className="block font-heading font-semibold text-h4">{t.name}</span>
              <span className="block text-base text-neutral-700">
                {t.memberCount} {t.memberCount === 1 ? 'member' : 'members'}
              </span>
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
}

function StepDateTime({
  date, startTime, onDateChange, onStartTimeChange,
}: {
  date: string; startTime: string;
  onDateChange: (d: string) => void; onStartTimeChange: (t: string) => void;
}) {
  return (
    <div className="max-w-[420px]">
      <label className="field">
        <span>Date</span>
        <input
          type="date" className="input" value={date}
          onChange={(e) => onDateChange(e.target.value)}
        />
      </label>
      <label className="field">
        <span>Start time</span>
        <input
          type="time" className="input" value={startTime}
          onChange={(e) => onStartTimeChange(e.target.value)}
        />
      </label>
    </div>
  );
}

/**
 * Step 4. §16 requires plain-language progression and no technical controls.
 *
 * The stages advance on a timer rather than on server events. That is deliberate
 * and is a display concern only: the plan request is a single call, and the
 * result is authoritative regardless of what this animation is showing. The
 * screen does not advance until the server responds.
 */
function StepPlanning({ planning, done }: { planning: boolean; done: boolean }) {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (!planning) return;
    setStage(0);
    const t = setInterval(
      () => setStage((s) => Math.min(s + 1, PLANNING_STAGES.length - 1)),
      700,
    );
    return () => clearInterval(t);
  }, [planning]);

  const reached = done ? PLANNING_STAGES.length : stage + 1;

  return (
    <ul aria-live="polite" className="max-w-[460px]">
      {PLANNING_STAGES.map((label, i) => {
        const complete = i < reached - (done ? 0 : 1) || done;
        const active = !done && i === stage;
        return (
          <li key={label} className="flex items-center gap-3 py-3 min-h-touch">
            <span
              className={[
                'w-6 h-6 rounded-full border flex items-center justify-center shrink-0',
                complete ? 'bg-accent border-accent text-white' : 'border-neutral-400',
              ].join(' ')}
              aria-hidden
            >
              {complete && <CheckIcon className="w-4 h-4" />}
            </span>
            <span
              className={[
                'text-base',
                complete ? 'text-text' : active ? 'text-text' : 'text-neutral-600',
              ].join(' ')}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function StepReview({
  review, onViewMap, onViewSchedule, onAdvancedAnalysis,
}: {
  review: PlanReview;
  onViewMap: () => void;
  onViewSchedule: () => void;
  onAdvancedAnalysis: () => void;
}) {
  return (
    <div>
      {/* §18: an estimate is never presented as a road-network result. */}
      {review.estimated && (
        <p className="mb-4 p-3 bg-alert-100 border border-alert-300 rounded-lg text-base">
          Drive times and distances are estimated. The road network was unavailable.
        </p>
      )}

      <dl className="grid grid-cols-2 desktop:grid-cols-4 gap-4 mb-6">
        <Stat label="Visits" value={String(review.visitCount)} />
        <Stat label="Stops" value={String(review.stopCount)} />
        <Stat label="Teams" value={String(review.teamCount)} />
        <Stat label="Road miles" value={String(review.roadMiles)} />
        <Stat label="Longest team day" value={review.longestTeamDayLabel} />
        <Stat label="Assignment" value={review.assignmentStatus} />
        <Stat
          label="Schedule conflicts"
          value={String(review.scheduleConflicts)}
          alert={review.scheduleConflicts > 0}
        />
        <Stat label="Route status" value={review.routeStatus} />
      </dl>

      <h6 className="text-accent-700 mb-2">Teams</h6>
      <ul className="mb-6">
        {review.teams.map((t) => (
          <li key={t.teamId} className="border-b border-neutral-300 py-3">
            <div className="flex items-baseline gap-2">
              <span className="font-heading font-semibold text-h4">{t.teamName}</span>
              <span className="text-base text-neutral-700">
                · {t.stopCount} stops · {t.visitCount} visits · {t.miles} mi
              </span>
              <span className="ml-auto text-base tabular-nums">{t.windowLabel}</span>
            </div>
            {t.warning && (
              <p className="text-base text-alert-600 mt-1">{t.warning}</p>
            )}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        <button className="btn btn-secondary" onClick={onViewMap}>View Map</button>
        <button className="btn btn-secondary" onClick={onViewSchedule}>View Schedule</button>
        <button className="btn btn-ghost" onClick={onAdvancedAnalysis}>Advanced Analysis</button>
      </div>
    </div>
  );
}

function Stat({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div>
      <dt className="text-h6 uppercase text-neutral-700 mb-1">{label}</dt>
      <dd
        className={[
          'font-heading font-semibold text-h4',
          alert ? 'text-alert-600' : 'text-text',
        ].join(' ')}
      >
        {value}
      </dd>
    </div>
  );
}

/** §6: the wizard footer replaces the bottom navigation while the wizard is open. */
function WizardFooter({
  step, canAdvance, planning, onBack, onNext,
}: {
  step: WizardStep; canAdvance: boolean; planning: boolean;
  onBack: () => void; onNext: () => void;
}) {
  return (
    <div
      className="fixed bottom-0 inset-x-0 z-30 bg-bg border-t border-neutral-300
                 pb-[env(safe-area-inset-bottom)]"
    >
      <div className="mx-auto max-w-content px-4 py-3 flex gap-3">
        <button
          className="btn btn-secondary flex-1"
          onClick={onBack}
          disabled={step === 1 || planning}
        >
          Back
        </button>
        <button
          className="btn btn-primary flex-1"
          onClick={onNext}
          disabled={!canAdvance || planning}
        >
          {step === 3 ? 'Plan' : step === 5 ? 'Open Team Routes' : 'Next'}
        </button>
      </div>
    </div>
  );
}
